/**
 * Question Card Component
 * Phase 4 - Reusable question display with inline editing support
 * 
 * Features:
 * - Shows question with answer
 * - Inline editing for consultants (if allowed by gridBehavior)
 * - Highlight support for wizard grouping
 * - Grid behavior enforcement
 */

import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { InlineQuestionEditor } from './InlineQuestionEditor';
import { CheckCircle2, Wand2, AlertCircle } from 'lucide-react';

interface QuestionCardProps {
  question: {
    questionId: string;
    questionText: string;
    answer?: string;
    answered?: boolean;
    required?: boolean;
    confidence?: string;
    answeredAt?: string;
    source?: string;
    gridBehavior?: 'inline' | 'wizard_only' | 'both';
  };
  configItemId: string;
  companyId: string;
  accessToken: string;
  isConsultant: boolean;
  canEditInline?: boolean;
  onAnswerUpdated: (questionId: string, newAnswer: string) => void;
  isHighlighted?: boolean;
  onOpenWizard?: () => void;
}

export function QuestionCard({
  question,
  configItemId,
  companyId,
  accessToken,
  isConsultant,
  canEditInline = true,
  onAnswerUpdated,
  isHighlighted = false,
  onOpenWizard
}: QuestionCardProps) {
  const showWizardOnlyWarning = question.gridBehavior === 'wizard_only' && !canEditInline;
  const showBothOptions = question.gridBehavior === 'both';

  return (
    <div 
      className={`p-4 border rounded-lg transition-all duration-200 ${
        isHighlighted 
          ? 'border-indigo-300 bg-indigo-50 shadow-sm' 
          : 'border-gray-200 hover:border-gray-400 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {/* Question Header */}
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-sm text-gray-900">
              {question.questionText}
            </h4>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {question.required && (
                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                  Required
                </Badge>
              )}
              {question.confidence && (
                <Badge 
                  variant={
                    question.confidence === 'high' ? 'default' : 
                    question.confidence === 'medium' ? 'secondary' : 
                    'outline'
                  }
                  className="text-xs"
                >
                  {question.confidence} confidence
                </Badge>
              )}
            </div>
          </div>
          
          {/* Answer Section */}
          <div className="space-y-2">
            {/* Inline Editor or Read-Only Display */}
            {isConsultant && canEditInline ? (
              <InlineQuestionEditor
                configItemId={configItemId}
                companyId={companyId}
                questionId={question.questionId}
                questionText={question.questionText}
                currentAnswer={question.answer || ''}
                accessToken={accessToken}
                onAnswerUpdated={(newAnswer) => onAnswerUpdated(question.questionId, newAnswer)}
                mode="inline"
              />
            ) : showWizardOnlyWarning ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded border">
                  {question.answer || 'No answer yet'}
                </p>
                <div className="flex items-start gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-700">
                  <Wand2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    This question can only be edited through the wizard to ensure proper context and validation.
                  </span>
                </div>
                {onOpenWizard && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onOpenWizard}
                    className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Open Wizard to Edit
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-700">{question.answer || 'No answer'}</p>
            )}

            {/* "Both" Option Tip */}
            {showBothOptions && canEditInline && isConsultant && (
              <div className="flex items-center gap-2 text-xs text-gray-500 bg-blue-50 p-2 rounded">
                <span>Tip: You can answer here or use the wizard for a guided experience</span>
              </div>
            )}
          </div>
          
          {/* Metadata Footer */}
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
            {question.answeredAt && (
              <span>
                Answered: {new Date(question.answeredAt).toLocaleDateString()}
              </span>
            )}
            {question.source && (
              <Badge variant="outline" className="text-xs">
                {question.source === 'wizard' ? 'Guided Wizard' :
                 question.source === 'discovery_session' ? 'Discovery Session' :
                 question.source === 'ai_assistant' ? 'AI Assistant' :
                 'Manual Entry'}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}